import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Logo } from '../design-system/components';
import { useAuth } from '../lib/auth';
import type { ApiError } from '../lib/api';

/**
 * Lands a kid straight into one chat room from their personal magic link —
 * no family code, screen name, or PIN. The link's token is exchanged for a
 * room-scoped session, then we jump to the chat.
 */
export function MagicEnter() {
  const { token = '' } = useParams();
  const nav = useNavigate();
  const { enterWithMagic } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against double-invoke in StrictMode
    ran.current = true;
    enterWithMagic(token)
      .then((roomId) => nav(`/chat/${roomId}`, { replace: true }))
      .catch((err) => setError(magicError(err as ApiError)));
  }, [token, enterWithMagic, nav]);

  return (
    <div className="kz-center">
      <Card style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
          <Logo height={40} />
        </div>
        {error ? (
          <>
            <h3 style={{ marginBottom: 'var(--space-2)' }}>Hmm, that link didn't work</h3>
            <p className="kz-muted">{error}</p>
          </>
        ) : (
          <p className="kz-muted">Taking you to your chat… ✨</p>
        )}
      </Card>
    </div>
  );
}

function magicError(e: ApiError): string {
  if (e.error === 'room_closed') return 'This chat has closed. Ask a grown-up when the next one opens.';
  if (e.error === 'unavailable') return 'This sign-in link is no longer active. Ask a grown-up for help.';
  return 'This link is invalid or has expired. Ask a grown-up for a fresh one.';
}
